// FIG Network (DESIGN_SPEC §11.4.8; RESTYLE §2): the tables Hosts (the ledger) · HTTP · DNS · Connections · Flows, each
// counted. OS background is hidden by default and always counted, with a per-service breakdown and a "show" toggle.
//   default NetworkFig (FigProps)    one full-width journal cell
//   meta(ctx) → { id: "network", label: "Network", count: hosts outside the background, empty: no network event }
//   NetworkLive ({ ctx })            the cockpit's compact version: hosts so far (and the other tables) up to now
import { useMemo, useState } from "react";
import { Check, Empty, FigHead, Seg, Tabs, figTitleId, tabPanelProps, type TabItem } from "@/components/ui";
import { pickArtifact, useArtifacts, useClock } from "@/hooks";
import { breakdown } from "@/lib/derive/noise";
import { capitalize, count, plural } from "@/lib/format";
import type { FigMeta, FigProps, NoiseIndex, ReportCtx } from "@/lib/derive/types";
import { noiseOf, t0Of } from "../derive/fold";
import { ledgerOf, type HostLedgerRow } from "../derive/hosts";
import { FlowBytesDialog } from "./FlowBytesDialog";
import { HostLedger, type Axis } from "./HostLedger";
import { HttpBodyDialog } from "./HttpBodyDialog";
import { ConnTable, DnsTable, FlowTable, HttpTable } from "./NetTables";
import type { HttpRow } from "../derive/hosts";

type TabId = "hosts" | "http" | "dns" | "conns" | "flows";

export function meta(ctx: ReportCtx): FigMeta {
  const noise = noiseOf(ctx);
  const n = noise.network ? ledgerOf(ctx).hosts.filter((h) => !h.background).length : 0;
  return { id: "network", label: "Network", count: n, empty: noise.network === 0 };
}

/** T0 (or the earliest contact shown) → collection or the last contact; live runs to the timeout, with a now line. */
function axisOf(ctx: ReportCtx, hosts: readonly HostLedgerRow[], now: number): { axis: Axis; nowT?: number } {
  const t0 = t0Of(ctx) ?? now;
  let from = 0;
  let to = 0;
  for (const h of hosts) {
    if (h.contacts.length) {
      from = Math.min(from, h.contacts[0]);
      to = Math.max(to, h.contacts[h.contacts.length - 1]);
    }
  }
  if (ctx.mode === "live") {
    const nowT = now - t0;
    return { axis: { from, to: Math.max(to, nowT, (ctx.job.timeout_secs || 0) * 1000) }, nowT };
  }
  const collect = ctx.stream.transitions.find((t) => t.state === "collecting");
  const events = ctx.stream.snap.events;
  const lastAt = events.length ? Date.parse(events[events.length - 1].ts) - t0 : 0;
  return { axis: { from, to: Math.max(to, collect && Number.isFinite(collect.ts) ? collect.ts - t0 : Number.isFinite(lastAt) ? lastAt : 0, 1000) } };
}

function HiddenNote({ noise, hosts, hidden, onToggle }: { noise: NoiseIndex; hosts: number; hidden: boolean; onToggle: () => void }) {
  if (!noise.total) return null;
  const parts = [hosts ? `${count(hosts)} background ${plural(hosts, "host")}` : "", `${count(noise.total)} ${plural(noise.total, "event")}`].filter(Boolean).join(" and ");
  return (
    <p className="hidden-note">
      {hidden ? `${capitalize(parts)} hidden` : `${capitalize(parts)} shown, faint`} ({breakdown(noise, 6)}) ·{" "}
      <button type="button" onClick={onToggle}>
        {hidden ? "show" : "hide"}
      </button>
    </p>
  );
}

function useNetTables(ctx: ReportCtx, live: boolean) {
  const ledger = ledgerOf(ctx);
  const noise = noiseOf(ctx);
  const [hideBg, setHideBg] = useState(true);
  const [tab, setTab] = useState<TabId>("hosts");
  const [inspect, setInspect] = useState<{ ip: string; port?: number } | null>(null);
  const [httpBodyRow, setHttpBodyRow] = useState<HttpRow | null>(null);
  const now = useClock(1000, live);
  const keep = <T extends { background: boolean }>(rows: readonly T[]) => (hideBg ? rows.filter((r) => !r.background) : rows);
  const hosts = keep(ledger.hosts);
  const tables = { hosts, http: keep(ledger.http), dns: keep(ledger.dns), conns: keep(ledger.conns), flows: keep(ledger.flows) };
  const { axis, nowT } = axisOf(ctx, hosts, now);
  const bgHosts = ledger.hosts.length - ledger.hosts.filter((h) => !h.background).length;
  // Hex view is a post-collection action — the pcap is only there once
  // the capture finishes. During live runs we skip the "bytes" link.
  const { data: artifacts } = useArtifacts(ctx.job.id, { enabled: !live });
  const pcapSha = useMemo(() => pickArtifact(artifacts, "pcap")?.sha256, [artifacts]);
  const onInspect = !live ? (ip: string, port?: number) => setInspect({ ip, port }) : undefined;
  const tabs: TabItem<TabId>[] = (
    [
      ["hosts", "Hosts"],
      ["http", "HTTP"],
      ["dns", "DNS"],
      ["conns", "Connections"],
      ["flows", "Flows"],
    ] as const
  ).map(([id, label]) => ({ id, label, count: count(tables[id].length) }));
  const body =
    tab === "hosts" ? (
      hosts.length ? (
        <HostLedger rows={hosts} axis={axis} live={live} now={nowT} onInspect={onInspect} />
      ) : null
    ) : tab === "http" ? (
      <HttpTable rows={tables.http} onInspectBody={!live ? setHttpBodyRow : undefined} />
    ) : tab === "dns" ? (
      <DnsTable rows={tables.dns} />
    ) : tab === "conns" ? (
      <ConnTable rows={tables.conns} onInspect={onInspect} />
    ) : (
      <FlowTable rows={tables.flows} onInspect={onInspect} />
    );
  const dialog = (
    <>
      {inspect && (
        <FlowBytesDialog
          open={true}
          jobID={ctx.job.id}
          pcapSha={pcapSha}
          dstIp={inspect.ip}
          dstPort={inspect.port}
          onClose={() => setInspect(null)}
        />
      )}
      <HttpBodyDialog open={!!httpBodyRow} row={httpBodyRow} onClose={() => setHttpBodyRow(null)} />
    </>
  );
  return { ledger, noise, hideBg, setHideBg, tab, setTab, tabs, tables, body, bgHosts, dialog };
}

/** The one "show" for background traffic is the breakdown line under the table (HiddenNote). */
function NoSampleTraffic({ noise, live }: { noise: NoiseIndex; live: boolean }) {
  return (
    <Empty inline rule={false}>
      No traffic from the sample{live ? " yet" : ""}
      {noise.total > 0 && ` · ${count(noise.total)} background ${plural(noise.total, "event")} hidden`}
    </Empty>
  );
}

export default function NetworkFig({ ctx, no }: FigProps) {
  const live = ctx.mode === "live";
  const t = useNetTables(ctx, live);
  const own = t.ledger.hosts.filter((h) => !h.background);
  return (
    <section className="cell fig" aria-labelledby={figTitleId(no)}>
      <FigHead no={no} label="Network" count={`${count(own.length)} ${plural(own.length, "host")}${live ? " so far" : ""}`} />
      <div className="fig-tools">
        <Tabs label="Network tables" idBase={`net-${no}`} tabs={t.tabs} value={t.tab} onChange={t.setTab} className="min-w-0" />
        <Check checked={t.hideBg} onChange={t.setHideBg} count={count(t.noise.total)}>
          Hide OS background
        </Check>
      </div>
      <div {...tabPanelProps(`net-${no}`, t.tab)} className="mt-3 min-w-0 focus-visible:outline-offset-4">
        {t.tab === "hosts" && !t.tables.hosts.length ? <NoSampleTraffic noise={t.noise} live={live} /> : t.body}
      </div>
      <HiddenNote noise={t.noise} hosts={t.bgHosts} hidden={t.hideBg} onToggle={() => t.setHideBg(!t.hideBg)} />
      {t.dialog}
    </section>
  );
}

/** The cockpit's Network tab: hosts so far, the other tables one click away; nothing scored. */
export function NetworkLive({ ctx }: { ctx: ReportCtx }) {
  const t = useNetTables(ctx, true);
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2.5">
        <Seg label="Network table" value={t.tab} onChange={t.setTab} options={t.tabs.map((x) => ({ value: x.id, label: x.label, count: x.count }))} />
        <Check checked={t.hideBg} onChange={t.setHideBg} count={count(t.noise.total)}>
          Hide OS background
        </Check>
      </div>
      <div className="mt-3 min-w-0">{t.tab === "hosts" && !t.tables.hosts.length ? <NoSampleTraffic noise={t.noise} live /> : t.body}</div>
      <HiddenNote noise={t.noise} hosts={t.bgHosts} hidden={t.hideBg} onToggle={() => t.setHideBg(!t.hideBg)} />
      {t.dialog}
    </div>
  );
}
